import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskAPI, projectAPI, teamAPI } from '../services/api';
import { COGNITIVE_LANES, COGNITIVE_LEVELS, teamMembers, tasks as mockTasks, projects as mockProjects } from '../mock';
import { useLabDemo } from '../context/LabDemoContext';
import { LabOriginBadge, LabIndicatorDot } from '../components/LabBadges';
import CreateTaskModal from '../components/CreateTaskModal';
import { 
  Plus, X, Calendar, Clock, Target, ChevronRight, Layers, 
  AlertTriangle, CheckSquare, ArrowUpDown, MoreHorizontal,
  Phone, Flag, ExternalLink, Users, Filter, Eye, FlaskConical,
  Shield, Sparkles, Building2
} from 'lucide-react';
import TaskPhasesSection, { PhaseProgressBadge } from '../components/TaskPhasesSection';
import { DESIGN } from '../components/DesignSystem';
import { 
  CollabBadge, 
  LaneCompositionDots, 
  AvatarCluster, 
  getMultiLaneGradient,
  CollabDetailModal,
  PremiumAvatar
} from './Collab';
import { 
  DemoRoleSwitcher, 
  UNIQUE_IP_COLORS,
  CHIEF_OF_STAFF_COLORS,
  getTasksRoleData 
} from '../components/focus';

// ================================================
// HELPERS
// ================================================
const getLane = (laneId) => COGNITIVE_LANES.find(l => l.id === laneId);
const getMember = (memberId) => teamMembers.find(m => m.id === memberId);

// Check if task is a collaboration
const isCollabTask = (task) => {
  return (task.contributors && task.contributors.length >= 1) || 
         (task.supporting_lanes && task.supporting_lanes.length > 0);
};

// ================================================
// COLLAB TASK PREVIEW TOOLTIP
// ================================================
const CollabPreviewTooltip = ({ task, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const primaryLane = getLane(task.primary_lane);
  const supportingLanes = task.supporting_lanes?.map(lId => getLane(lId)).filter(Boolean) || [];
  const owner = getMember(task.owner_id);
  const contributors = task.contributors?.map(id => getMember(id)).filter(Boolean) || [];
  const allMembers = [owner, ...contributors].filter(Boolean);
  const mockProgress = useMemo(() => Math.floor(Math.random() * 40) + 50, [task.id]);
  
  return (
    <div 
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      
      {showTooltip && (
        <div 
          className="absolute z-50 left-0 bottom-full mb-2 w-72 p-4 rounded-xl shadow-2xl"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <CollabBadge size="sm" />
            <span className="text-sm font-bold truncate" style={{ color: DESIGN.colors.textPrimary }}>
              {task.title}
            </span>
          </div>
          
          {/* Lane composition */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>Lanes:</span>
            <LaneCompositionDots primaryLane={primaryLane} supportingLanes={supportingLanes} size="sm" />
          </div>
          
          {/* Team avatars */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>Team:</span>
            <div className="flex -space-x-2">
              {allMembers.slice(0, 4).map((member, idx) => (
                <PremiumAvatar key={member?.id || idx} member={member} size="sm" showCapacity={false} />
              ))}
              {allMembers.length > 4 && (
                <span className="text-xs font-medium ml-1" style={{ color: DESIGN.colors.textMuted }}>
                  +{allMembers.length - 4}
                </span>
              )}
            </div>
          </div>
          
          {/* Progress */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>Progress:</span>
            <div className="flex items-center gap-2">
              <div className="w-20 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
                <div 
                  className="h-full rounded-full"
                  style={{ 
                    width: `${mockProgress}%`, 
                    backgroundColor: primaryLane?.color 
                  }}
                />
              </div>
              <span className="text-xs font-bold" style={{ color: primaryLane?.color }}>{mockProgress}%</span>
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t" style={{ borderColor: DESIGN.colors.border }}>
            <span className="text-xs" style={{ color: DESIGN.colors.primary }}>
              Click to open full collaboration view
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ================================================
// TASK DETAIL SIDE PANEL
// ================================================
const TaskDetailPanel = ({ task, onClose, projects }) => {
  const navigate = useNavigate();
  const lane = COGNITIVE_LANES.find(l => l.id === task?.primary_lane);
  const level = COGNITIVE_LEVELS.find(l => l.id === task?.required_level);
  const project = projects?.find(p => p.id === task?.project_id);
  
  const statusConfigs = {
    'Planned': { bg: DESIGN.colors.background, text: DESIGN.colors.textSecondary, glow: 'none' },
    'Active': { bg: 'rgba(0, 201, 167, 0.1)', text: DESIGN.colors.primary, glow: `0 0 15px ${DESIGN.colors.primary}30` },
    'In Review': { bg: DESIGN.colors.amberSubtle, text: '#D97706', glow: '0 0 15px rgba(245, 158, 11, 0.3)' },
    'Completed': { bg: DESIGN.colors.successSubtle, text: DESIGN.colors.success, glow: '0 0 15px rgba(16, 185, 129, 0.3)' }
  };
  const statusConfig = statusConfigs[task?.status] || statusConfigs.Planned;

  if (!task) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="task-detail-panel">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      
      <div 
        className="relative w-full max-w-lg shadow-2xl overflow-y-auto animate-slide-in"
        style={{ backgroundColor: DESIGN.colors.card }}
      >
        {/* Header */}
        <div 
          className="sticky top-0 z-10 p-6 border-b"
          style={{ backgroundColor: DESIGN.colors.card, borderColor: DESIGN.colors.border }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-3" style={{ color: DESIGN.colors.textPrimary }}>
                {task.title}
              </h2>
              <span 
                className="inline-flex items-center gap-2 text-sm px-4 py-1.5 rounded-full font-semibold"
                style={{ backgroundColor: statusConfig.bg, color: statusConfig.text, boxShadow: statusConfig.glow }}
              >
                {task.status}
              </span>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 rounded-xl transition-colors hover:bg-gray-100"
            >
              <X className="w-5 h-5" style={{ color: DESIGN.colors.textMuted }} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>
              Description
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: DESIGN.colors.textPrimary }}>
              {task.description || 'No description provided'}
            </p>
          </div>
          
          {/* Assignee */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: DESIGN.colors.textMuted }}>
              Assignee
            </h3>
            {task.assignee ? (
              <div 
                className="flex items-center gap-4 p-4 rounded-xl transition-all hover:shadow-md cursor-pointer"
                style={{ backgroundColor: DESIGN.colors.background }}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-base font-bold"
                  style={{ 
                    background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                    color: 'white',
                    boxShadow: DESIGN.shadows.glowPrimary
                  }}
                >
                  {task.assignee.avatar}
                </div>
                <div>
                  <p className="text-base font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
                    {task.assignee.name}
                  </p>
                  <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>Task Owner</p>
                </div>
              </div>
            ) : (
              <p className="text-sm p-4 rounded-xl" style={{ color: DESIGN.colors.textMuted, backgroundColor: DESIGN.colors.background }}>
                Unassigned
              </p>
            )}
          </div>
          
          {/* Project Link */}
          {task.project_id && project && (
            <button 
              onClick={() => { onClose(); navigate(`/projects/${task.project_id}`); }}
              className="w-full flex items-center justify-between p-4 rounded-xl transition-all hover:shadow-md group"
              style={{ backgroundColor: DESIGN.colors.primarySubtle }}
            >
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5" style={{ color: DESIGN.colors.primary }} />
                <div className="text-left">
                  <p className="text-sm font-semibold" style={{ color: DESIGN.colors.primary }}>
                    {project.name}
                  </p>
                  <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Parent Project</p>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: DESIGN.colors.primary }} />
            </button>
          )}
          
          {/* Lane & Level */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>
                Lane
              </h3>
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: lane?.color, boxShadow: `0 0 10px ${lane?.color}60` }}
                />
                <span className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
                  {lane?.name || task.primary_lane}
                </span>
              </div>
            </div>
            <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>
                Level
              </h3>
              <span 
                className="text-lg font-black"
                style={{ color: level?.id === 'L4' ? '#8B5CF6' : level?.id === 'L3' ? '#3B82F6' : DESIGN.colors.textPrimary }}
              >
                {level?.id || task.required_level}
              </span>
            </div>
          </div>
          
          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>
                Due Date
              </h3>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
                <span className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>
                  {task.due_date 
                    ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) 
                    : 'Not set'}
                </span>
              </div>
            </div>
            <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>
                Created
              </h3>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
                <span className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>
                  {task.created_at 
                    ? new Date(task.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) 
                    : 'Unknown'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Definition of Success */}
          {task.definition_of_success && (
            <div 
              className="p-4 rounded-xl border-l-4"
              style={{ backgroundColor: DESIGN.colors.amberSubtle, borderLeftColor: DESIGN.colors.amber }}
            >
              <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#92400E' }}>
                Definition of Success
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: '#78350F' }}>
                {task.definition_of_success}
              </p>
            </div>
          )}
          
          {/* Task Phases Section */}
          <div className="pt-4 border-t" style={{ borderColor: DESIGN.colors.border }}>
            <TaskPhasesSection taskId={task.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ================================================
// MINI PHASE PROGRESS BAR
// ================================================
const MiniPhaseProgress = ({ completed, total, hasOverdue }) => {
  const percentage = total > 0 ? (completed / total) * 100 : 0;
  const color = hasOverdue ? DESIGN.colors.error : completed === total ? DESIGN.colors.success : DESIGN.colors.primary;
  
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
        <div 
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${percentage}%`,
            backgroundColor: color,
            boxShadow: `0 0 6px ${color}60`
          }}
        />
      </div>
      <span 
        className="text-xs font-semibold"
        style={{ color: hasOverdue ? DESIGN.colors.error : DESIGN.colors.textSecondary }}
      >
        {completed}/{total}
      </span>
      {hasOverdue && (
        <AlertTriangle className="w-3.5 h-3.5" style={{ color: DESIGN.colors.error }} />
      )}
    </div>
  );
};

// ================================================
// COLLAB FILTER BUTTON
// ================================================
const CollabFilterButton = ({ active, onClick, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${active ? '' : 'hover:bg-white/50'}`}
    style={{
      background: active 
        ? 'linear-gradient(135deg, #8B5CF6 0%, #A855F7 50%, #C084FC 100%)'
        : 'transparent',
      color: active ? 'white' : DESIGN.colors.textSecondary,
      boxShadow: active ? '0 4px 15px rgba(139, 92, 246, 0.4)' : 'none'
    }}
  >
    <Users className="w-4 h-4" />
    Collabs
    {count > 0 && (
      <span 
        className="text-xs font-bold px-1.5 py-0.5 rounded-full"
        style={{ 
          backgroundColor: active ? 'rgba(255,255,255,0.2)' : 'rgba(139, 92, 246, 0.15)',
          color: active ? 'white' : '#8B5CF6'
        }}
      >
        {count}
      </span>
    )}
  </button>
);

// ================================================
// MAIN TASKS PAGE
// ================================================
const Tasks = () => {
  const navigate = useNavigate();
  const { labTasks } = useLabDemo();
  const [statusFilter, setStatusFilter] = useState('All');
  const [collabFilter, setCollabFilter] = useState('all'); // 'all', 'collab', 'standard', 'lab'
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedCollabTask, setSelectedCollabTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembersData, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCollabCreation, setShowCollabCreation] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  
  // Demo role switcher state
  const [activeRole, setActiveRole] = useState('ceo');
  const [demoData, setDemoData] = useState(null);

  const fetchData = async () => {
    try {
      const [tasksRes, projectsRes, teamRes] = await Promise.all([
        taskAPI.getAll(),
        projectAPI.getAll(),
        teamAPI.getAll()
      ]);
      const fetchedTasks = tasksRes.data.tasks || [];
      const fetchedProjects = projectsRes.data.projects || [];
      const fetchedTeam = teamRes.data.team_members || [];
      
      // Use API data if available, otherwise fall back to mock data
      setTasks(fetchedTasks.length > 0 ? fetchedTasks : mockTasks);
      setProjects(fetchedProjects.length > 0 ? fetchedProjects : mockProjects);
      setTeamMembers(fetchedTeam.length > 0 ? fetchedTeam : teamMembers);
    } catch (error) {
      console.error('Error fetching tasks, using mock data:', error);
      // Use mock data as fallback
      setTasks(mockTasks);
      setProjects(mockProjects);
      setTeamMembers(teamMembers);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Role change handler
  const handleRoleChange = useCallback((newRole) => {
    setActiveRole(newRole);
    const roleData = getTasksRoleData(newRole);
    setDemoData(roleData);
  }, []);

  const isUniqueIP = demoData?.isUniqueIP;
  const isChiefOfStaff = demoData?.isChiefOfStaff;
  const accentColor = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primary 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primary 
      : DESIGN.colors.primary;
  const accentDark = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primaryDark 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primaryDark 
      : DESIGN.colors.primaryDark;
  const accentSubtle = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primarySubtle 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primarySubtle 
      : DESIGN.colors.primarySubtle;
  const accentGlow = isChiefOfStaff 
    ? `0 0 20px ${CHIEF_OF_STAFF_COLORS.primaryGlow}` 
    : isUniqueIP 
      ? `0 0 20px ${UNIQUE_IP_COLORS.primaryGlow}` 
      : DESIGN.shadows.glowPrimary;

  // Chief of Staff entity filter state
  const [entityFilter, setEntityFilter] = useState('all'); // 'all', 'company', 'soulfood'

  // Merge LAB-created tasks with API tasks (LAB tasks at top if exist)
  // If demo data is active, use demo tasks instead
  const allTasks = useMemo(() => {
    // If demo role is active and has tasks, use demo tasks
    if (demoData?.tasks) {
      // Apply entity filter for Chief of Staff
      if (isChiefOfStaff && entityFilter !== 'all') {
        return demoData.tasks.filter(t => t.entity === entityFilter);
      }
      return demoData.tasks;
    }
    
    if (labTasks && labTasks.length > 0) {
      // Remove any existing LAB tasks to avoid duplicates, then add fresh ones at top
      const filteredApiTasks = tasks.filter(t => !t.id?.startsWith('lab-task-'));
      return [...labTasks, ...filteredApiTasks];
    }
    return tasks;
  }, [tasks, labTasks, demoData, isChiefOfStaff, entityFilter]);

  // Collab task count
  const collabTaskCount = useMemo(() => 
    allTasks.filter(t => isCollabTask(t)).length
  , [allTasks]);

  // LAB task count
  const labTaskCount = useMemo(() => 
    allTasks.filter(t => t.isLabGenerated).length
  , [allTasks]);

  const filteredTasks = useMemo(() => {
    return allTasks.filter(t => {
      // Status filter
      if (statusFilter !== 'All') {
        if (statusFilter === 'Outstanding') {
          if (t.status !== 'Active' && t.status !== 'Planned') return false;
        } else if (t.status !== statusFilter) {
          return false;
        }
      }
      
      // Collab filter
      if (collabFilter === 'collab' && !isCollabTask(t)) return false;
      if (collabFilter === 'standard' && isCollabTask(t)) return false;
      if (collabFilter === 'lab' && !t.isLabGenerated) return false;
      
      return true;
    });
  }, [allTasks, statusFilter, collabFilter]);

  const getProject = (projectId) => projects.find(p => p.id === projectId);

  // Handle task click - open Collab modal for collab tasks
  const handleTaskClick = (task) => {
    if (isCollabTask(task)) {
      setSelectedCollabTask(task);
    } else {
      setSelectedTask(task);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div 
              className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin"
              style={{ borderColor: `${DESIGN.colors.primary}30`, borderTopColor: DESIGN.colors.primary }}
            />
          </div>
          <p className="text-sm font-medium" style={{ color: DESIGN.colors.textMuted }}>Loading tasks...</p>
        </div>
      </div>
    );
  }

  const statusConfigs = {
    'Planned': { bg: DESIGN.colors.background, text: DESIGN.colors.textSecondary, glow: 'none' },
    'Active': { bg: 'rgba(0, 201, 167, 0.1)', text: DESIGN.colors.primary, glow: `0 0 10px ${DESIGN.colors.primary}30` },
    'In Review': { bg: 'rgba(59, 130, 246, 0.1)', text: '#2563EB', glow: '0 0 10px rgba(59, 130, 246, 0.3)' },
    'Completed': { bg: DESIGN.colors.successSubtle, text: DESIGN.colors.success, glow: '0 0 10px rgba(16, 185, 129, 0.3)' }
  };

  const filters = ['All', 'Outstanding', 'Active', 'In Review', 'Planned', 'Completed'];

  return (
    <div className="space-y-8" data-testid="tasks-page">
      {/* Demo Role Switcher */}
      <DemoRoleSwitcher 
        activeRole={activeRole} 
        onRoleChange={handleRoleChange}
      />
      
      {/* Premium Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${accentColor} 0%, ${accentDark} 100%)`,
                boxShadow: accentGlow
              }}
            >
              {isChiefOfStaff ? <Shield className="w-5 h-5 text-white" /> : <CheckSquare className="w-5 h-5 text-white" />}
            </div>
            <h1 
              className="text-3xl font-black tracking-tight"
              style={{ color: DESIGN.colors.textPrimary }}
            >
              {demoData?.isMyTasks ? 'MY TASKS' : 'TASKS'}
            </h1>
            {isChiefOfStaff && (
              <span 
                className="text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5"
                style={{ 
                  backgroundColor: CHIEF_OF_STAFF_COLORS.primarySubtle,
                  color: CHIEF_OF_STAFF_COLORS.primary
                }}
              >
                <Shield className="w-3 h-3" />
                Executive Coordination
              </span>
            )}
            {demoData?.scope_label && !isChiefOfStaff && (
              <span 
                className="text-xs font-bold px-3 py-1.5 rounded-full"
                style={{ 
                  backgroundColor: accentSubtle,
                  color: accentColor
                }}
              >
                {demoData.scope_label}
              </span>
            )}
          </div>
          <p className="text-base" style={{ color: DESIGN.colors.textSecondary }}>
            {demoData?.summary 
              ? isChiefOfStaff 
                ? `${demoData.summary.total} total • ${demoData.summary.company_tasks} Company • ${demoData.summary.soulfood_tasks} Soul Food`
                : `${demoData.summary.total} total • ${demoData.summary.active || demoData.summary.remaining_today || 0} active`
              : `All tasks across projects • ${filteredTasks.length} items`
            }
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Chief of Staff Dual-Entity Filter */}
          {isChiefOfStaff && demoData?.hasDualEntityFilter && (
            <div 
              className="flex items-center gap-1 p-1 rounded-xl"
              style={{ backgroundColor: DESIGN.colors.background }}
            >
              <button
                onClick={() => setEntityFilter('all')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all`}
                style={{
                  backgroundColor: entityFilter === 'all' ? CHIEF_OF_STAFF_COLORS.primary : 'transparent',
                  color: entityFilter === 'all' ? 'white' : DESIGN.colors.textSecondary
                }}
              >
                All Tasks
              </button>
              <button
                onClick={() => setEntityFilter('company')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5`}
                style={{
                  backgroundColor: entityFilter === 'company' ? '#1E3A5F' : 'transparent',
                  color: entityFilter === 'company' ? 'white' : DESIGN.colors.textSecondary
                }}
              >
                <Building2 className="w-3 h-3" />
                Company
              </button>
              <button
                onClick={() => setEntityFilter('soulfood')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5`}
                style={{
                  backgroundColor: entityFilter === 'soulfood' ? UNIQUE_IP_COLORS.primary : 'transparent',
                  color: entityFilter === 'soulfood' ? 'white' : DESIGN.colors.textSecondary
                }}
              >
                <Sparkles className="w-3 h-3" />
                Soul Food
              </button>
            </div>
          )}
          
          {/* New Task dropdown */}
          <div className="relative group">
            <button 
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5"
              style={{ 
                background: `linear-gradient(135deg, ${accentColor} 0%, ${accentDark} 100%)`,
                color: 'white',
                boxShadow: accentGlow
              }}
            >
              <Plus className="w-5 h-5" />
              New Task
            </button>
            
            {/* Dropdown for creating Collab */}
            <div 
              className="absolute top-full right-0 mt-2 w-56 rounded-xl overflow-hidden shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20"
              style={{ backgroundColor: DESIGN.colors.card }}
            >
              <button
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
                style={{ color: DESIGN.colors.textPrimary }}
                onClick={() => setShowCreateTaskModal(true)}
                data-testid="new-standard-task-btn"
              >
                <CheckSquare className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
                Standard Task
              </button>
              <button
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors border-t"
                style={{ color: DESIGN.colors.textPrimary, borderColor: DESIGN.colors.border }}
                onClick={() => navigate('/collab')}
              >
                <Users className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                New Collaboration
                <CollabBadge size="sm" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Filter Tabs with Collab Filter */}
      <div className="flex items-center justify-between">
        <div 
          className="inline-flex items-center gap-1 p-1.5 rounded-2xl"
          style={{ backgroundColor: DESIGN.colors.background }}
        >
          {filters.map((status) => (
            <button 
              key={status} 
              onClick={() => setStatusFilter(status)} 
              className="px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300"
              style={{ 
                backgroundColor: statusFilter === status ? DESIGN.colors.primary : 'transparent',
                color: statusFilter === status ? 'white' : DESIGN.colors.textSecondary,
                boxShadow: statusFilter === status ? DESIGN.shadows.glowPrimary : 'none'
              }}
            >
              {status}
            </button>
          ))}
        </div>
        
        {/* Collab Filter */}
        <div 
          className="inline-flex items-center gap-1 p-1.5 rounded-2xl"
          style={{ backgroundColor: DESIGN.colors.background }}
        >
          <button
            onClick={() => setCollabFilter('all')}
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={{
              backgroundColor: collabFilter === 'all' ? DESIGN.colors.card : 'transparent',
              color: collabFilter === 'all' ? DESIGN.colors.textPrimary : DESIGN.colors.textSecondary,
              boxShadow: collabFilter === 'all' ? DESIGN.shadows.sm : 'none'
            }}
          >
            All Tasks
          </button>
          <CollabFilterButton 
            active={collabFilter === 'collab'} 
            onClick={() => setCollabFilter('collab')}
            count={collabTaskCount}
          />
          {/* LAB Filter Button */}
          {labTaskCount > 0 && (
            <button
              onClick={() => setCollabFilter('lab')}
              className="px-4 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2"
              style={{
                backgroundColor: collabFilter === 'lab' ? DESIGN.colors.card : 'transparent',
                color: collabFilter === 'lab' ? DESIGN.colors.primary : DESIGN.colors.textSecondary,
                boxShadow: collabFilter === 'lab' ? `0 0 15px ${DESIGN.colors.primary}20` : 'none',
                border: collabFilter === 'lab' ? `1px solid ${DESIGN.colors.primary}30` : '1px solid transparent'
              }}
            >
              <FlaskConical className="w-4 h-4" />
              LAB Created
              <span 
                className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                style={{ 
                  backgroundColor: `${DESIGN.colors.primary}20`,
                  color: DESIGN.colors.primary
                }}
              >
                {labTaskCount}
              </span>
            </button>
          )}
          <button
            onClick={() => setCollabFilter('standard')}
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={{
              backgroundColor: collabFilter === 'standard' ? DESIGN.colors.card : 'transparent',
              color: collabFilter === 'standard' ? DESIGN.colors.textPrimary : DESIGN.colors.textSecondary,
              boxShadow: collabFilter === 'standard' ? DESIGN.shadows.sm : 'none'
            }}
          >
            Standard Only
          </button>
        </div>
      </div>

      {/* Premium Data Table */}
      <div 
        className="rounded-2xl overflow-hidden"
        style={{ 
          backgroundColor: DESIGN.colors.card,
          boxShadow: DESIGN.shadows.lg
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr 
                style={{ 
                  background: 'linear-gradient(135deg, rgba(0, 201, 167, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)'
                }}
              >
                {[
                  { label: 'Task', sortable: true },
                  { label: 'Project', sortable: true },
                  { label: 'Assignee', sortable: true },
                  { label: 'Lane', sortable: true },
                  { label: 'Phases', sortable: false },
                  { label: 'Status', sortable: true },
                  { label: 'Due Date', sortable: true }
                ].map((col, idx) => (
                  <th 
                    key={idx}
                    className="text-left px-5 py-4 text-xs font-bold uppercase tracking-wider border-b cursor-pointer group"
                    style={{ color: DESIGN.colors.textMuted, borderColor: DESIGN.colors.border }}
                  >
                    <div className="flex items-center gap-2">
                      {col.label}
                      {col.sortable && (
                        <ArrowUpDown 
                          className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ color: DESIGN.colors.primary }}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task, idx) => {
                const project = getProject(task.project_id);
                const lane = getLane(task.primary_lane);
                const statusConfig = statusConfigs[task.status] || statusConfigs.Planned;
                const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'Completed';
                const isCollab = isCollabTask(task);
                const isLab = task.isLabGenerated;
                
                // Get collab-specific data
                const primaryLane = getLane(task.primary_lane);
                const supportingLanes = task.supporting_lanes?.map(lId => getLane(lId)).filter(Boolean) || [];
                const owner = getMember(task.owner_id);
                const contributors = task.contributors?.map(id => getMember(id)).filter(Boolean) || [];
                const allMembers = [owner, ...contributors].filter(Boolean);
                const borderGradient = isCollab ? getMultiLaneGradient(primaryLane, supportingLanes) : undefined;
                
                // LAB-created tasks also get a gradient border
                const labBorderGradient = isLab ? `linear-gradient(180deg, ${DESIGN.colors.primary} 0%, #06B6D4 100%)` : undefined;
                
                // Chief of Staff entity border colors
                const entityBorderColor = isChiefOfStaff && task.entity 
                  ? task.entity === 'soulfood' 
                    ? UNIQUE_IP_COLORS.primary 
                    : '#1E3A5F' 
                  : null;

                const rowContent = (
                  <tr 
                    key={task.id} 
                    onClick={() => handleTaskClick(task)} 
                    className="cursor-pointer transition-all duration-200 group relative"
                    style={{ 
                      backgroundColor: isLab 
                        ? `${DESIGN.colors.primary}05` // Teal tint for LAB rows
                        : isCollab 
                          ? `${primaryLane?.color}04` // Very subtle tint for collab rows
                          : idx % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.01)',
                      borderBottom: `1px solid ${DESIGN.colors.borderSubtle}`
                    }}
                    data-testid={`task-row-${task.id}`}
                  >
                    {/* Chief of Staff entity left border */}
                    {isChiefOfStaff && entityBorderColor && !isCollab && !isLab && (
                      <td className="w-1.5 p-0" style={{ backgroundColor: entityBorderColor }} />
                    )}
                    {/* Multi-lane gradient left border for collab tasks */}
                    {isCollab && !isLab && (
                      <td className="w-1.5 p-0" style={{ background: borderGradient }} />
                    )}
                    {/* LAB origin left border */}
                    {isLab && (
                      <td className="w-1.5 p-0" style={{ background: labBorderGradient }} />
                    )}
                    
                    {/* Task */}
                    <td className={`px-5 py-4 ${isCollab ? '' : ''}`} colSpan={isCollab || isLab ? 1 : 1}>
                      <div className="flex items-start gap-3">
                        {/* LAB Indicator dot */}
                        {isLab && !task.has_overdue_phase && (
                          <div className="flex-shrink-0 mt-1.5">
                            <LabIndicatorDot tooltip={true} />
                          </div>
                        )}
                        {task.has_overdue_phase && (
                          <div 
                            className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 animate-pulse"
                            style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                          >
                            <AlertTriangle className="w-3.5 h-3.5" style={{ color: DESIGN.colors.error }} />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p 
                              className="text-sm font-bold group-hover:text-teal-600 transition-colors"
                              style={{ color: DESIGN.colors.textPrimary }}
                            >
                              {task.title}
                            </p>
                            {/* LAB Origin badge */}
                            {isLab && <LabOriginBadge size="sm" />}
                            {/* Collab badge */}
                            {isCollab && !isLab && <CollabBadge size="sm" />}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs line-clamp-1" style={{ color: DESIGN.colors.textMuted }}>
                              {task.description?.substring(0, 50)}...
                            </p>
                            {/* Lane composition dots for collab */}
                            {isCollab && supportingLanes.length > 0 && (
                              <LaneCompositionDots 
                                primaryLane={primaryLane} 
                                supportingLanes={supportingLanes} 
                                size="sm" 
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Project */}
                    <td className="px-5 py-4">
                      <span 
                        className="text-sm font-medium cursor-pointer hover:underline"
                        style={{ color: DESIGN.colors.primary }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (project) navigate(`/projects/${project.id}`);
                        }}
                      >
                        {project?.name || '-'}
                      </span>
                    </td>
                    
                    {/* Assignee - Different treatment for collab */}
                    <td className="px-5 py-4">
                      {isCollab ? (
                        // Overlapping avatar cluster for collab tasks
                        <div className="flex items-center">
                          <div className="flex -space-x-2">
                            {allMembers.slice(0, 3).map((member, mIdx) => (
                              <PremiumAvatar 
                                key={member?.id || mIdx} 
                                member={member} 
                                isOwner={mIdx === 0}
                                size="sm" 
                                showCapacity={true}
                                className="hover:z-10"
                              />
                            ))}
                          </div>
                          {allMembers.length > 3 && (
                            <span 
                              className="ml-1.5 text-xs font-bold px-2 py-1 rounded-lg"
                              style={{ backgroundColor: DESIGN.colors.background, color: DESIGN.colors.textMuted }}
                            >
                              +{allMembers.length - 3}
                            </span>
                          )}
                        </div>
                      ) : task.assignee ? (
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold"
                            style={{ 
                              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                              color: 'white'
                            }}
                          >
                            {task.assignee.avatar}
                          </div>
                          <span className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>
                            {task.assignee.name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm" style={{ color: DESIGN.colors.textMuted }}>Unassigned</span>
                      )}
                    </td>
                    
                    {/* Lane */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full transition-transform group-hover:scale-125"
                          style={{ 
                            backgroundColor: lane?.color,
                            boxShadow: `0 0 8px ${lane?.color}60`
                          }}
                        />
                        <span className="text-sm font-medium" style={{ color: DESIGN.colors.textSecondary }}>
                          {lane?.name || task.primary_lane}
                        </span>
                      </div>
                    </td>
                    
                    {/* Phases */}
                    <td className="px-5 py-4">
                      {task.phase_count > 0 ? (
                        <MiniPhaseProgress 
                          completed={task.completed_phase_count || 0}
                          total={task.phase_count}
                          hasOverdue={task.has_overdue_phase}
                        />
                      ) : (
                        <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>—</span>
                      )}
                    </td>
                    
                    {/* Status */}
                    <td className="px-5 py-4">
                      <span 
                        className="inline-flex items-center text-xs px-3 py-1.5 rounded-full font-bold"
                        style={{ 
                          backgroundColor: statusConfig.bg, 
                          color: statusConfig.text,
                          boxShadow: statusConfig.glow
                        }}
                      >
                        {task.status}
                      </span>
                    </td>
                    
                    {/* Due Date */}
                    <td className="px-5 py-4">
                      <span 
                        className={`text-sm font-medium ${isOverdue ? 'font-bold' : ''}`}
                        style={{ color: isOverdue ? DESIGN.colors.error : DESIGN.colors.textSecondary }}
                      >
                        {task.due_date 
                          ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) 
                          : '-'}
                      </span>
                    </td>
                  </tr>
                );

                // Wrap collab tasks with preview tooltip
                return isCollab ? (
                  <CollabPreviewTooltip key={task.id} task={task}>
                    {rowContent}
                  </CollabPreviewTooltip>
                ) : rowContent;
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Standard Task Detail Panel */}
      {selectedTask && (
        <TaskDetailPanel 
          task={selectedTask} 
          onClose={() => setSelectedTask(null)} 
          projects={projects}
        />
      )}
      
      {/* Collab Detail Modal for collab tasks */}
      {selectedCollabTask && (
        <CollabDetailModal 
          task={selectedCollabTask} 
          onClose={() => setSelectedCollabTask(null)} 
        />
      )}

      {/* CSS Animations */}
      <style>{`
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        tr:hover {
          background-color: rgba(0, 201, 167, 0.03) !important;
        }
      `}</style>

      {/* Create Task Modal */}
      <CreateTaskModal 
        isOpen={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        onSuccess={() => {
          fetchData(); // Refresh tasks list
        }}
        projects={projects}
      />
    </div>
  );
};

export default Tasks;
